import { useContext, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link,
  IconButton,
  Card,
  CardContent,
  Badge,
  Stack,
  CircularProgress,
} from "@mui/material";
import { ArrowForward as ArrowForwardIcon, ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import useAfasData from "../hooks/useAfasData";
import RecruitmentEntry from "./Recruitment/RecruitmentEntry";
import { JobVacancy } from "../types/types";
import { InSiteContext } from "../context/InSiteContext";
import { LanguageContext } from "../context/LanguageContext";

const RecruitmentComponent: React.FC = () => {
  const { strings } = useContext(LanguageContext);
  const inSiteUrl = useContext(InSiteContext);
  const itemsPerPage = 4;
  const [vacancies, setVacancies] = useState<JobVacancy[]>([]);
  const { data: vacanciesOverview = [], isLoading: isLoadingVacancies } =
    useAfasData("/recruitment/overview");

  useEffect(() => {
    if (!isLoadingVacancies && vacanciesOverview.length > 0) {
      setVacancies(vacanciesOverview);
    }
  }, [vacanciesOverview]);

  const [page, setPage] = useState(1);
  const totalPages =
    Math.ceil(vacancies.length / itemsPerPage) > 0 ? Math.ceil(vacancies.length / itemsPerPage) : 1;
  const currentVacancies = vacancies.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  return (
    <Card sx={{ height: "100%" }}>
      <CardContent sx={{ px: 0 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box display="flex" alignItems="center">
            <Badge color="warning" badgeContent={vacancies.length}>
              <Typography variant="h3" component="h3">
                {strings.recruitment.title}
              </Typography>
            </Badge>
          </Box>

          <Link
            color="initial"
            href={`${inSiteUrl}/recruitment`}
            target="_parent"
            underline="hover"
            sx={{ textDecoration: "underline", fontWeight: 400 }}
          >
            {strings.recruitment.goto} &gt;
          </Link>
        </Box>

        {/* Vacancies section */}
        <Box mt={4}>
          <Typography variant="h6" sx={{ textTransform: "uppercase" }}>
            {strings.recruitment.open}
          </Typography>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ pb: 2, pr: 1, pl: 0 }}>
                    <Typography variant="overline">{strings.recruitment.name}</Typography>
                  </TableCell>
                  <TableCell sx={{ pb: 2, px: 1 }}>
                    <Typography variant="overline">{strings.recruitment.end}</Typography>
                  </TableCell>
                  <TableCell sx={{ pb: 2, px: 1 }}>
                    <Typography variant="overline">{strings.recruitment.candidates}</Typography>
                  </TableCell>
                  <TableCell sx={{ pb: 2, px: 1 }}>
                    <Typography variant="overline">{strings.recruitment.myreviews}</Typography>
                  </TableCell>
                  <TableCell sx={{ pb: 2, px: 1 }}>
                    <Typography variant="overline">{strings.recruitment.chairperson}</Typography>
                  </TableCell>
                  <TableCell sx={{ pb: 2, pl: 1, pr: 0 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoadingVacancies ? (
                  <Box mt={2} width={"64px"} height={"64px"}>
                    <CircularProgress />
                  </Box>
                ) : (
                  currentVacancies.map((vacancy) => <RecruitmentEntry vacancy={vacancy} key={vacancy.id} />)
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalPages !== 1 && (
            <Stack sx={{ mt: 2 }}>
              <Box display="flex" justifyContent={"flex-end"} alignItems="center">
                <IconButton onClick={(e) => handlePageChange(e, page - 1)} disabled={page === 1} size="small">
                  <ArrowBackIcon />
                </IconButton>
                <Typography component="span" sx={{ mx: 1 }}>
                  {page}/
                  <Typography component={"span"} fontWeight={"bold"}>
                    {totalPages}
                  </Typography>
                </Typography>
                <IconButton
                  onClick={(e) => handlePageChange(e, page + 1)}
                  disabled={page === totalPages}
                  size="small"
                >
                  <ArrowForwardIcon />
                </IconButton>
              </Box>
            </Stack>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default RecruitmentComponent;
